<i18n src="@/locales/components/CbsDynamicFormConfig.json"></i18n>

<template>
	<div v-if="value && value.length" class="dynamic-form-content">
		<div :key="indexForm" v-for="(field, indexForm) in value">
			<!-- RADIO -->
			<!-- DROPDOWN -->
			<div class="flex" v-if="field.control === $constant.request.control.radio || field.control === $constant.request.control.dropdown">
				<label class="w-1/2 sm:w-1/4 text-gray-1">{{ field.title }}:</label>
				<div class="text-black w-1/2 sm:w-3/4">
					{{ renderOptionsControlValue(field) }}
				</div>
			</div>

			<!-- RELATION -->
			<div class="flex" v-if="field.control === $constant.request.control.relation">
				<label class="w-1/2 sm:w-1/4 text-gray-1">{{ field.title }}:</label>
				<div class="text-black w-1/2 sm:w-3/4" v-if="field.value && field.value.length">
					{{
						field.value
							.map(id => {
								const parsedAttributes = typeof field.attributes === "string" ? JSON.parse(field.attributes) : field.attributes;
								const selectedOption = (
									(relations[parsedAttributes.related_model.split("\\").join("")] && relations[parsedAttributes.related_model.split("\\").join("")].data) ||
									[]
								).find(option => option.id === id);
								return (selectedOption && selectedOption.content) || "";
							})
							.join(", ")
					}}
				</div>
			</div>

			<!-- CHECKBOX -->
			<div class="flex" v-if="field.control === $constant.request.control.checkbox">
				<label class="w-1/2 sm:w-1/4 text-gray-1">{{ field.title }}:</label>
				<div class="text-black w-1/2 sm:w-3/4">{{ renderOptionsControlValue(field, true) }}</div>
			</div>

			<!-- TEXT -->
			<!-- DATETIME PICKER -->
			<div class="flex" v-if="field.control === $constant.request.control.autofill || field.control === $constant.request.control.text || field.control === $constant.request.control.datetime">
				<label class="w-1/2 sm:w-1/4 text-gray-1">{{ field.title }}:</label>
				<div v-if="field.control !== $constant.request.control.datetime" :class="['w-1/2 sm:w-3/4 text-black']" v-text="field.value"></div>
				<div v-else class="w-1/2 sm:w-3/4 text-black">{{ field.value | dateTime(userDateTimeFormat) }}</div>
			</div>

			<!-- TEXTAREA -->
			<div class="flex" v-if="field.control === $constant.request.control.textarea">
				<label class="w-1/2 sm:w-1/4 text-gray-1">{{ field.title }}:</label>
				<div class="w-1/2 sm:w-3/4 text-black whitespace-pre-wrap" v-html="field.value"></div>
			</div>

			<!-- NUMBER -->
			<div class="flex" v-if="field.control === $constant.request.control.number">
				<label class="w-1/2 sm:w-1/4 text-gray-1">{{ field.title }}:</label>
				<div class="w-1/2 sm:w-3/4 text-black">
					<cbs-masked-input disabled dataType="int" class="masked-input-readonly" :userSetting="userSettings" v-model.number="field.value" />
					<p v-if="typeof field.attributes === 'string' ? JSON.parse(field.attributes).is_readable : false">
						<span class="text-gray-2"> {{ $t("NumberToWord") }}: {{ $helper.numberToWords(field.value) }} </span>
					</p>
				</div>
			</div>

			<!-- CALCULATOR -->
			<div class="flex" v-if="field.control === $constant.request.control.calculator">
				<label class="w-1/2 sm:w-1/4 text-gray-1">{{ field.title }}:</label>
				<div v-if="typeof field.attributes === 'string' ? JSON.parse(field.attributes).show_as_text : false" class="w-1/2 sm:w-3/4 text-black">{{ $helper.numberToWords(field.value) }}</div>
				<div v-else class="w-1/2 sm:w-3/4 text-black">
					<cbs-masked-input disabled dataType="int" class="masked-input-readonly" :userSetting="userSettings" v-model.number="field.value" />
					<p v-if="typeof field.attributes === 'string' ? JSON.parse(field.attributes).is_readable : false">
						<span class="text-gray-2"> {{ $t("NumberToWord") }}: {{ $helper.numberToWords(field.value) }} </span>
					</p>
				</div>
			</div>

			<!-- ATTACH FILE -->
			<div class="flex" v-if="field.control === $constant.request.control.attach">
				<label class="flex-shrink-0 w-1/2 sm:w-1/4 text-gray-1">{{ field.title }}:</label>
				<cbs-upload-ref v-if="field.value && field.value.length > 0" :isEditable="false" :value="(field.value || []).map(fileId => mappedAttachObj[fileId])" />
			</div>

			<!-- REPEATER -->
			<template v-if="field.control === $constant.request.control.repeater">
				<repeater-control v-if="field && field.value && field.value.length" :fieldData="field" :isEditable="false" :value="field.value" :mappedAttachObj="mappedAttachObj" />
			</template>

			<!-- LABEL -->
			<label-content v-if="field.control === $constant.request.control.label" :value="field" />
		</div>
	</div>
</template>

<script>
import _ from "lodash";
import requestV2GQL from "@/http/requests/request/request_v2_gql";

import LabelContent from "./LabelContent";
import CbsUploadRef from "@/components/cbs-upload-ref/CbsUploadRef";

const isInvalidData = data => {
	if (!_.isObject(data)) {
		return true;
	}
	return !_.has(data, "id") || !_.has(data, "title") || !_.has(data, "value") || !_.has(data, "control") || !_.has(data, "children") || !_.has(data, "is_optional") || !_.has(data, "description");
};

export default {
	name: "DynamicFormContent",
	components: {
		CbsUploadRef,
		LabelContent,
		RepeaterControl: () => import("./RepeaterControl")
	},
	props: {
		value: {
			type: Array,
			validator: prop => {
				if (!_.isEmpty(prop)) {
					return !_.some(prop, data => {
						return isInvalidData(data);
					});
				}
				return true;
			}
		},
		mappedAttachObj: {
			type: Object,
			default() {
				return {};
			}
		}
	},
	emits: {
		showModalRepeaterData: {}
	},
	data() {
		return {
			relations: {},
			mappedRelationSelectedValues: {}
		};
	},
	computed: {
		stepTitles() {
			if (this.value && this.value.length) {
				return this.value.map(field => field.title);
			}

			return [];
		}
	},
	watch: {
		value() {
			this.setInitRelations();
		}
	},
	methods: {
		async setInitRelations() {
			if (!_.isEmpty(this.value)) {
				try {
					let isNeedUpdateRelationData = false;
					this.value.forEach(field => {
						const parsedAttributes = typeof field.attributes === "string" ? JSON.parse(field.attributes) : field.attributes;
						const existOptionIds = this.mappedRelationSelectedValues[parsedAttributes.related_model] || [];
						if (field.control === this.$constant.request.control.relation && !_.isEmpty(field.value)) {
							if (field.value.some(optionId => !existOptionIds.includes(optionId)) || _.isEmpty(existOptionIds)) {
								this.mappedRelationSelectedValues[parsedAttributes.related_model] = existOptionIds.concat(field.value);
								isNeedUpdateRelationData = true;
							}
						}
					});

					if (isNeedUpdateRelationData && !_.isEmpty(this.mappedRelationSelectedValues)) {
						this.$vs.loading();
						const response = await requestV2GQL.getCombinedRelationDataByModel(this.mappedRelationSelectedValues);
						this.relations = response || {};
					}
				} catch (error) {
					this.$helper.notifyError({ error, context: this });
				} finally {
					this.$nextTick(() => this.$vs.loading.close());
				}
			}
		},
		renderOptionsControlValue(controlData = {}, isMultipleValue = false) {
			const parsedAttributes = typeof controlData.attributes === "string" ? JSON.parse(controlData.attributes) : controlData.attributes;
			if (!parsedAttributes || !parsedAttributes.options) {
				return null;
			}
			if (!isMultipleValue) {
				return _.get(parsedAttributes.options.find(option => option.id == controlData.value) || {}, "content");
			}
			return controlData.value.map(id => _.get(parsedAttributes.options.find(option => option.id == id) || {}, "content")).join(", ");
		}
	},
	created() {
		this.setInitRelations();
	}
};
</script>

<style lang="scss" scoped>
.dynamic-form-content {
	.masked-input-readonly {
		border: 0;
		background: 0;
		height: auto;
	}
}
</style>
